import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type {ComponentRef} from "react";
import React, {forwardRef, useImperativeHandle, useRef} from "react";

import {useTimeout} from "../../react";
import {isReactElement} from "../../react/utils/isReactElement";
import FormAnchor from "../Form/FormAnchor";
import Label from "../Label";
import {Select} from "../Select";
import type {Props as SelectProps} from "../Select";
import {FormControl, FormControlError, FormControlErrorText} from "../ui/formcontrol";
import {HStack} from "../ui/hstack";

type Props = {
    testID?: string;
    name?: string;
    label?: string;
    size?: "xs" | "sm";
    tooltip?: React.ReactNode;
    error?: React.ReactNode;
    isDisabled?: boolean;
    className?: string;
    rightElement?: React.ReactNode;
    horizontal?: boolean;
    children: React.ReactNode;
} & StrictUnion<
    | {
          name?: string;
      }
    | {
          isInsideTab: true;
          name: string;
      }
>;

const formcontrolStyle = tva({
    base: "flex-shrink flex-grow",
    variants: {
        horizontal: {
            true: "flex-row items-center gap-4"
        }
    }
});

const FormField = forwardRef<{scrollIntoError: () => void}, Props>(function FormField(
    {
        testID = "form-field",
        name,
        error,
        isDisabled,
        children,
        label,
        size = "xs",
        className,
        tooltip,
        rightElement,
        horizontal
    },
    ref
) {
    let newChildren: React.ReactNode = children;

    if (isReactElement<SelectProps<any, any>>(children, Select)) {
        newChildren = React.cloneElement(children, {
            error: !!error
        });
    }

    const errorRef = useRef<ComponentRef<typeof FormControlError>>(null);
    const scrollAfterTimeout = useTimeout(() => {
        if (errorRef.current instanceof HTMLDivElement) {
            errorRef.current?.scrollIntoView({behavior: "auto", block: "center"});
        }
    }, 50);

    // offer a method to scroll into the error
    useImperativeHandle(
        ref,
        () => ({
            scrollIntoError: () => {
                // only support web for now
                if (process.env.IS_WEB) {
                    scrollAfterTimeout();
                }
            }
        }),
        [scrollAfterTimeout]
    );

    return (
        <FormControl
            className={formcontrolStyle({class: className, horizontal})}
            isDisabled={isDisabled}
            isInvalid={!!error}
            testID={testID}
        >
            {name && process.env.IS_WEB && <FormAnchor name={name} />}
            {(rightElement || !!label) && (
                <HStack className="items-center" space="2xs">
                    {!!label && (
                        <Label size={size} tooltip={tooltip}>
                            {label}
                        </Label>
                    )}
                    {rightElement}
                </HStack>
            )}
            {newChildren}
            <FormControlError ref={errorRef}>
                <FormControlErrorText>{error}</FormControlErrorText>
            </FormControlError>
        </FormControl>
    );
});

export default FormField;
